import React, { FC, useCallback, useMemo } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { ICartEntry } from 'features/cart/types';
import { useAddMappedEntryToCart } from 'features/menu/components/product-wizard/use-add-to-cart';
import {
  AddIconButton,
  StyledButtonBox,
  StyledRecentOrderCard,
} from 'features/menu/pages/recent-items-page/recent-order-card-list/recent-order-card-list-item/styled';

import ActionButton, { ActionButtonSizes } from 'components/action-button';
import ItemActions from 'components/item-actions';
import { ListItemGroup } from 'components/list-item-group';
import { Product } from 'components/product';
import { toaster } from 'components/toaster/toaster';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { primitive } from 'styles/constants';
import { routes } from 'utils/routing';

interface IMinimalFavoriteCardProps {
  id: string;
  name: string;
  minimalEntries: ICartEntry[];
  onEditFavorite: (id: string) => void;
  onRemoveFavorite: (id: string) => void;
}

const mapSelections = (entry: ICartEntry) => {
  if (!entry.children?.length) {
    return [];
  }

  return entry.children.map((child, index) => ({
    id: `${entry.id}-${child.id}-${index}`,
    element: `${child.quantity}x ${child.name}`,
  }));
};

export const MinimalFavoriteCard: FC<IMinimalFavoriteCardProps> = ({
  id,
  name,
  minimalEntries,
  onEditFavorite,
  onRemoveFavorite,
}) => {
  const addMappedEntryToCart = useAddMappedEntryToCart();
  const { replace } = useRouter();

  const handleAddSingleItem = useCallback(
    (entry: ICartEntry) => {
      if (!addMappedEntryToCart(entry)) {
        return;
      }

      logEvent(CustomEventNames.ADD_FAVORITE_TO_CART, EventTypes.UserContent, {
        'Product Name': entry?.name,
        'User Location': 'Favorites Tab',
      });
    },
    [addMappedEntryToCart]
  );

  const handleReorder = useCallback(() => {
    const addedEntries = minimalEntries.filter(entry => {
      const added = addMappedEntryToCart(entry, { skipSuccessToast: true });
      if (added) {
        logEvent(CustomEventNames.ADD_FAVORITE_TO_CART, EventTypes.UserContent, {
          'Product Name': entry?.name,
          'User Location': 'Favorites Tab',
        });
      }
      return added;
    });
    if (!addedEntries.length) {
      return;
    }

    toaster.addToCartToast(t('cartUpdatedSuccess', 'Your cart was updated'));
    replace(routes.cart);
  }, [addMappedEntryToCart, minimalEntries, replace]);

  const items = useMemo(
    () =>
      minimalEntries.map((entry, index) => (
        <StyledRecentOrderCard key={`${entry.cartId ?? entry.id}-${index}`}>
          <Product
            image={JSON.stringify(entry.image ?? {})}
            name={entry.name}
            quantity={entry.quantity}
            selections={mapSelections(entry)}
            displayMode="list"
            quantityBadgeTextStyle={{
              color: primitive.$mayoEggWhite,
            }}
            quantityBadgeStyle={{
              top: '-10px',
              height: '20px',
              width: '20px',
              justifyContent: 'center',
            }}
          />
          <StyledButtonBox>
            <AddIconButton
              variant="outline"
              onPress={() => handleAddSingleItem(entry)}
              icon={<Icon variant="add" />}
              disabled={false}
              testID="add-to-cart"
              accessibilityLabel={t('addToCart')}
            />
          </StyledButtonBox>
        </StyledRecentOrderCard>
      )),
    [handleAddSingleItem, minimalEntries]
  );

  if (!minimalEntries.length) {
    return null;
  }

  return (
    <ListItemGroup
      header={name || ''}
      headerAction={
        <ActionButton
          testID="reorder-button"
          onPress={handleReorder}
          disabled={!minimalEntries.length}
          size={ActionButtonSizes.SMALL}
        >
          {t('orderNow')}
        </ActionButton>
      }
      items={items}
      footer={
        <ItemActions onEdit={() => onEditFavorite(id)} onRemove={() => onRemoveFavorite(id)} />
      }
      key={id}
    />
  );
};
